import { ReactNode } from "react";
import { useAgentAccess } from "@/hooks/useAgentAccess";
import { Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface Props {
  agentId: string;
  agentLabel: string;
  children: ReactNode;
}

/**
 * Gates a dashboard agent page behind the per-agent access check.
 * Admins always pass. Otherwise the user needs a direct user grant or an
 * organization grant from `agent_access` (resolved via `user_has_agent_access`).
 */
export function AgentAccessGuard({ agentId, agentLabel, children }: Props) {
  const { hasAccess, loading } = useAgentAccess(agentId);
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="max-w-md rounded-2xl border bg-card p-8 text-center shadow-lg">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent/10">
            <Lock className="h-6 w-6 text-accent" />
          </div>
          <h2 className="text-lg font-bold text-foreground">{agentLabel} is not enabled for your account</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            During the Pharmalify beta, access to each AI agent is granted on request.
            Please contact the Pharmalify team to request access to <span className="font-medium">{agentLabel}</span>.
          </p>
          <Button variant="outline" className="mt-4" onClick={() => navigate("/contact")}>
            Contact Pharmalify
          </Button>
          <Button className="mt-6" onClick={() => navigate("/dashboard/agents")}>
            Back to Agents
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
